/**
 * AuthGuard component - Protects routes requiring authentication
 * Redirects to /login if user is not authenticated
 * Redirects to /waitlist if user is not approved (when requireApproval=true)
 */

import React, { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { VerifyEmail } from './VerifyEmail';
import { isLeavingForLogin, useAuthHandover } from './authProject';
import { usePresence } from '../../hooks/usePresence';

interface AuthGuardProps {
  children: React.ReactNode;
  requireApproval?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children, requireApproval = true }) => {
  const { user, isAuthenticated, loading, isApproved, emailVerified } = useAuth();
  useAuthHandover(user?.uid, !loading && isAuthenticated && emailVerified);
  usePresence(!loading && isAuthenticated && emailVerified);

  useEffect(() => {
    if (!loading) {
      // Redirect to login if not authenticated (preserve return path)
      if (!isAuthenticated) {
        if (isLeavingForLogin()) return;
        console.error('[AUTH] AuthGuard: not authenticated, redirecting to /login. Path:', window.location.pathname);
        const returnPath = window.location.pathname + window.location.search;
        const loginUrl = returnPath && returnPath !== '/'
          ? `/login?redirect=${encodeURIComponent(returnPath)}`
          : '/login';
        window.location.href = loginUrl;
        return;
      }

      // Redirect to waitlist if approval is required but user is not approved.
      // Verification takes precedence — unverified users see the verify screen
      // (rendered below), not the waitlist.
      if (requireApproval && !isApproved && emailVerified) {
        // Only redirect if not already on waitlist page
        if (!window.location.pathname.startsWith('/waitlist')) {
          window.location.href = '/waitlist';
        }
      }
    }
  }, [isAuthenticated, loading, isApproved, emailVerified, requireApproval]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Show nothing while redirecting to login
  if (!isAuthenticated) {
    return null;
  }

  // Block unverified email/password users behind the verification screen.
  // Google users are emailVerified=true, so this never gates them.
  if (!emailVerified) {
    return <VerifyEmail />;
  }

  // Show nothing while redirecting to waitlist
  if (requireApproval && !isApproved) {
    return null;
  }

  // Render protected content
  return <>{children}</>;
};
